import { useState, useEffect } from 'react'
import { useGetIdentity } from '@refinedev/core'
import { StatCard } from '@/components/dashboard/StatCard'
import {
  InterviewTypeChart,
  SubscriptionStatusChart,
  TopCompaniesByInterviewsChart,
  TopCompaniesByUsersChart,
  TopAgenciesByOrgsChart,
  CompaniesWithSubscriptionChart,
  CompaniesGrowthChart,
  UsersGrowthChart,
  ApplicationsGrowthChart,
  InterviewsGrowthChart,
  SubscriptionsGrowthChart,
  CandidatesGrowthChart,
  PlatformGrowthOverviewChart,
} from '@/components/dashboard/DashboardCharts'
import {
  QuickCreateAgencyModal,
  QuickSendEmailModal,
  QuickSendWhatsAppModal,
} from '@/components/dashboard/QuickActionModals'
import { Building2, Mail, MessageCircle, ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { flaskApi } from '@/lib/flaskApi'

const OVERVIEW_STATS = [
  { label: 'Total Companies',        resource: 'companies',
    accent: 'bg-sky-50 dark:bg-sky-900/30 text-[#076698] dark:text-sky-300' },
  { label: 'Total Users',            resource: 'users',
    accent: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-300' },
  { label: 'Total Candidates',       resource: 'local_candidates',
    accent: 'bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-300' },
  { label: 'Total Agencies',         resource: 'agencies',
    accent: 'bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-300' },
  { label: 'Active Subscriptions',   resource: 'subscriptions',
    filters: [{ field: 'active', operator: 'eq', value: true }] as const,
    accent: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-300' },
  { label: 'Inactive Subscriptions', resource: 'subscriptions',
    filters: [{ field: 'active', operator: 'eq', value: false }] as const,
    accent: 'bg-rose-50 dark:bg-rose-900/20 text-rose-500 dark:text-rose-300' },
  { label: 'Total Positions',        resource: 'company_positions',
    accent: 'bg-orange-50 dark:bg-orange-900/20 text-orange-500 dark:text-orange-300' },
  { label: 'Total Applications',     resource: 'applications',
    accent: 'bg-sky-50 dark:bg-sky-900/30 text-[#076698] dark:text-sky-300' },
  { label: 'Total Requisitions',     resource: 'requisitions',
    accent: 'bg-slate-100 dark:bg-slate-700/40 text-slate-600 dark:text-slate-300' },
]

const QUICK_ACTIONS = [
  {
    id: 'create-agency' as const,
    label: 'Create Agency',
    description: 'Set up a new agency — mandatory fields only',
    icon: Building2,
    color: 'text-[#076698] dark:text-sky-400',
    bg:    'bg-[#076698]/10 dark:bg-sky-900/30',
    border: 'border-[#076698]/20 dark:border-sky-800/40',
  },
  {
    id: 'send-email' as const,
    label: 'Send Email',
    description: 'Queue a one-off email to any address',
    icon: Mail,
    color: 'text-amber-600 dark:text-amber-400',
    bg:    'bg-amber-50 dark:bg-amber-900/20',
    border: 'border-amber-200/50 dark:border-amber-800/30',
  },
  {
    id: 'send-whatsapp' as const,
    label: 'Send WhatsApp',
    description: 'Send a WhatsApp template message',
    icon: MessageCircle,
    color: 'text-emerald-600 dark:text-emerald-400',
    bg:    'bg-emerald-50 dark:bg-emerald-900/20',
    border: 'border-emerald-200/50 dark:border-emerald-800/30',
  },
]

type ActiveModal = 'create-agency' | 'send-email' | 'send-whatsapp' | null

export function DashboardPage() {
  const { data: identity } = useGetIdentity<{ username: string }>()
  const [activeModal, setActiveModal] = useState<ActiveModal>(null)

  return (
    <div className="space-y-8 w-full max-w-screen-2xl mx-auto">

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 md:col-span-8 bento-card p-8 relative overflow-hidden flex flex-col justify-between min-h-[180px]">
          <div className="relative z-10">
            <span className="inline-block bg-[#076698]/10 text-[#076698] dark:bg-sky-900/40 dark:text-sky-300 px-3 py-1 rounded-full text-[10px] font-extrabold tracking-widest uppercase mb-3">
              Platform Dashboard
            </span>
            <h2 className="text-3xl font-extrabold tracking-tight text-slate-800 dark:text-slate-50 leading-tight mb-2">
              Welcome back{identity?.username ? `, ${identity.username}` : ''}.
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed max-w-md">
              Full platform health at a glance. Use quick actions below to create agencies, send emails and WhatsApp messages without leaving this page.
            </p>
          </div>
          <div className="absolute -right-8 -top-8 w-64 h-64 rounded-full bg-[#076698]/5 dark:bg-sky-500/10 blur-3xl pointer-events-none" />
          <div className="absolute right-0 top-0 h-full w-1/3 bg-gradient-to-l from-sky-50/60 dark:from-sky-900/10 to-transparent pointer-events-none" />
        </div>

        <div className="col-span-12 md:col-span-4 grid grid-rows-2 gap-4">
          <StatCard label="Total Companies" resource="companies"
            accent="bg-sky-50 dark:bg-sky-900/30 text-[#076698] dark:text-sky-300" />
          <StatCard label="Active Subscriptions" resource="subscriptions"
            filters={[{ field: 'active', operator: 'eq', value: true }]}
            accent="bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-300" />
        </div>
      </div>

      <section>
        <p className="section-label mb-3">Quick Actions</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {QUICK_ACTIONS.map(({ id, label, description, icon: Icon, color, bg, border }) => (
            <button
              key={id}
              type="button"
              onClick={() => setActiveModal(id)}
              className={cn(
                'bento-card bento-card-hover p-5 flex items-start gap-4 text-left w-full group',
                'border', border,
              )}
            >
              <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center shrink-0', bg)}>
                <Icon size={18} className={color} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={cn('text-sm font-bold mb-0.5', color)}>{label}</p>
                <p className="text-xs text-slate-400 dark:text-slate-500 leading-relaxed">{description}</p>
              </div>
              <ArrowRight size={14} className={cn('shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity', color)} />
            </button>
          ))}
        </div>
      </section>

      <section>
        <p className="section-label mb-3">Platform Overview</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {OVERVIEW_STATS.map(({ label, resource, filters, accent }) => (
            <StatCard key={label} label={label} resource={resource}
              filters={filters as never} accent={accent} />
          ))}
        </div>
      </section>

      <section>
        <div className="flex items-center gap-3 mb-3">
          <p className="section-label">Growth Trends</p>
          <span className="text-[10px] text-slate-400 dark:text-slate-500">Year-over-year across all key metrics</span>
        </div>
        <div className="mb-4">
          <PlatformGrowthOverviewChart />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <CompaniesGrowthChart />
          <UsersGrowthChart />
          <CandidatesGrowthChart />
          <ApplicationsGrowthChart />
          <InterviewsGrowthChart />
          <SubscriptionsGrowthChart />
        </div>
      </section>

      <section>
        <div className="flex items-center gap-3 mb-3">
          <p className="section-label">Operational Distribution</p>
          <span className="text-[10px] text-slate-400 dark:text-slate-500">Subscription coverage, type breakdowns</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <CompaniesWithSubscriptionChart />
          <SubscriptionStatusChart />
          <InterviewTypeChart />
        </div>
      </section>

      <section>
        <div className="flex items-center gap-3 mb-3">
          <p className="section-label">Top Entities</p>
          <span className="text-[10px] text-slate-400 dark:text-slate-500">Most active companies and agencies</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <TopCompaniesByInterviewsChart />
          <TopCompaniesByUsersChart />
          <TopAgenciesByOrgsChart />
        </div>
      </section>

      <section>
        <div className="flex items-center gap-3 mb-3">
          <p className="section-label">LinkedIn Search Monitoring</p>
          <span className="text-[10px] text-slate-400 dark:text-slate-500">Apify usage across companies</span>
        </div>
        <LinkedinDashboardSummary />
      </section>

      <QuickCreateAgencyModal
        open={activeModal === 'create-agency'}
        onClose={() => setActiveModal(null)}
      />
      <QuickSendEmailModal
        open={activeModal === 'send-email'}
        onClose={() => setActiveModal(null)}
      />
      <QuickSendWhatsAppModal
        open={activeModal === 'send-whatsapp'}
        onClose={() => setActiveModal(null)}
      />
    </div>
  )
}

function LinkedinDashboardSummary() {
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    flaskApi.get('/linkedin-monitoring/summary')
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  if (loading || !data) {
    return <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
      {[1,2,3,4].map((i) => <div key={i} className="bento-card p-4 animate-pulse"><div className="h-4 w-24 bg-slate-200 rounded mb-2" /><div className="h-6 w-12 bg-slate-300 rounded" /></div>)}
    </div>
  }

  const lowQuota = (data.companies || []).filter((c: any) => c.status === 'low' || c.status === 'exhausted')

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div className="bento-card p-4">
          <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400 mb-1">LinkedIn Companies</div>
          <div className="text-xl font-bold text-slate-800">{data.total_companies}</div>
        </div>
        <div className="bento-card p-4">
          <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400 mb-1">Searches Remaining</div>
          <div className="text-xl font-bold text-slate-800">{data.total_remaining.toLocaleString()}</div>
        </div>
        <div className="bento-card p-4">
          <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400 mb-1">Est. Apify Cost</div>
          <div className="text-xl font-bold text-slate-800">${(data.total_remaining * 0.15).toFixed(0)}</div>
        </div>
        <div className={`bento-card p-4 ${lowQuota.length > 0 ? 'border-amber-300 bg-amber-50/50' : ''}`}>
          <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400 mb-1">Low Quota Alerts</div>
          <div className="text-xl font-bold text-amber-600">{lowQuota.length}</div>
        </div>
      </div>
      {lowQuota.length > 0 && (
        <div className="bento-card p-4 border-amber-200 bg-amber-50/30">
          <p className="text-xs font-semibold text-amber-800 mb-2">Companies running low on LinkedIn search quota:</p>
          <div className="flex flex-wrap gap-2">
            {lowQuota.map((c: any) => (
              <span key={c.company_id} className="text-[11px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">
                {c.company_name} ({c.quota_remaining} left)
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
